import { useEffect, useRef, useState } from "react";
import { Facebook, ExternalLink } from "lucide-react";
import { SectionHeading } from "./SectionHeading";

const FB_PAGE_URL = "https://www.facebook.com/ingelec.reunion";

/**
 * FacebookFeed
 * Affiche la timeline officielle de la page Facebook Ingelec via le Page Plugin
 * (iframe officielle Facebook). Aucun token ni API key requis.
 * Doc: https://developers.facebook.com/docs/plugins/page-plugin/
 */
export function FacebookFeed() {
  const wrapperRef = useRef(null);
  const [width, setWidth] = useState(500);

  useEffect(() => {
    const updateWidth = () => {
      if (!wrapperRef.current) return;
      // Facebook Page Plugin supporte 180 → 500px
      const w = Math.min(500, Math.max(280, wrapperRef.current.offsetWidth - 32));
      setWidth(Math.floor(w));
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const encodedHref = encodeURIComponent(FB_PAGE_URL);
  const iframeSrc =
    `https://www.facebook.com/plugins/page.php?` +
    `href=${encodedHref}` +
    `&tabs=timeline` +
    `&width=${width}` +
    `&height=700` +
    `&small_header=false` +
    `&adapt_container_width=true` +
    `&hide_cover=false` +
    `&show_facepile=true` +
    `&locale=fr_FR`;

  return (
    <section
      className="bg-slate-50 border-y border-slate-200"
      data-testid="facebook-feed-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHeading
          eyebrow="Suivez-nous sur Facebook"
          title="Notre actualité en direct"
          subtitle="Découvrez nos derniers chantiers, nos installations IRVE et les avis de nos clients"
          testId="facebook-feed-heading"
        />

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Left side — Branding / CTA */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1B3A6B] via-[#1B3A6B] to-[#0F172A] p-8 lg:p-10 text-white shadow-xl">
              <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#1877F2]/30 blur-3xl" />
              <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-[#FBBF24]/15 blur-3xl" />

              <div className="relative">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#1877F2] shadow-lg mb-6">
                  <Facebook className="w-7 h-7 text-white" strokeWidth={2.2} />
                </div>

                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
                  @ingelec.reunion
                </h3>
                <p className="mt-3 text-slate-300 leading-relaxed">
                  Rejoignez notre communauté pour ne rien manquer de nos
                  réalisations électrotechniques et de nos installations de
                  bornes de recharge sur l&apos;île de La Réunion.
                </p>

                <ul className="mt-6 space-y-2.5 text-sm text-slate-200">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                    Photos de chantiers récents
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                    Avis & retours clients
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                    Actualités IRVE et nouveautés
                  </li>
                </ul>

                <a
                  href={FB_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="facebook-follow-cta"
                  className="mt-8 inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold transition-colors shadow-lg shadow-blue-500/30"
                >
                  <Facebook className="w-4 h-4" />
                  Suivre la page
                  <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                </a>
              </div>
            </div>
          </div>

          {/* Right side — Live Facebook Page Plugin */}
          <div className="lg:col-span-3" ref={wrapperRef}>
            <div
              className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden p-3 sm:p-4 flex justify-center"
              data-testid="facebook-feed-embed"
            >
              <iframe
                title="Page Facebook Ingelec"
                src={iframeSrc}
                width={width}
                height={700}
                style={{ border: "none", overflow: "hidden", maxWidth: "100%" }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-xs text-slate-500 text-center">
              Flux officiel Facebook · Si le contenu ne s&apos;affiche pas, vos
              paramètres de confidentialité bloquent peut-être les cookies tiers
              Facebook.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FacebookFeed;
